"use client"

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useTheme } from '../../components/layout/ThemeProvider';
import OptimizedImage from '@/components/ui/OptimizedImage';
import { CATEGORIAS, NOME_CATEGORIAS } from '@/lib/constants';
import { motion } from 'framer-motion';

const ServicosPage = () => {
  const { theme } = useTheme();
  const [categoriaAtiva, setCategoriaAtiva] = useState(CATEGORIAS.TODOS);
  const [navSticky, setNavSticky] = useState(false);
  
  // Efeito para detectar scroll e fazer a navegação ficar sticky
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = 100; // Ajuste conforme necessário
      setNavSticky(scrollY > threshold);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Array de serviços
  const servicos = [
    {
      id: 'desenvolvimento-web',
      titulo: 'Desenvolvimento Web',
      categoria: CATEGORIAS.DESENVOLVIMENTO,
      descricao: 'Sites exclusivos e personalizados com foco em SEO, design moderno e segurança avançada.',
      imagem: '/images/desenvolvimento-bg.jpg',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      destaques: [
        'Websites responsivos para dispositivos móveis',
        'Otimização para SEO e desempenho',
        'Design moderno e intuitivo'
      ]
    },
    {
      id: 'desenvolvimento-sistemas',
      titulo: 'Sistemas Empresariais',
      categoria: CATEGORIAS.DESENVOLVIMENTO,
      descricao: 'Sistemas personalizados e dashboards para gestão e automação de processos empresariais.',
      imagem: '/images/sistemas-bg.jpg',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
      ),
      destaques: [
        'Sistemas de gestão empresarial',
        'Integrações com APIs externas',
        'Automação de processos'
      ]
    },
    {
      id: 'energia-solar',
      titulo: 'Energia Solar',
      categoria: CATEGORIAS.INFRAESTRUTURA,
      descricao: 'Reduza até 95% da sua conta de luz com soluções completas de energia solar sustentável.',
      imagem: '/images/energia-solar-bg.jpg',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="5"></circle>
          <line x1="12" y1="1" x2="12" y2="3"></line>
          <line x1="12" y1="21" x2="12" y2="23"></line>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
          <line x1="1" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="12" x2="23" y2="12"></line>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
      ),
      destaques: [
        'Projetos residenciais e comerciais',
        'Instalação profissional certificada',
        'Retorno do investimento em até 5 anos'
      ]
    },
    {
      id: 'infraestrutura-rede',
      titulo: 'Redes Empresariais',
      categoria: CATEGORIAS.INFRAESTRUTURA,
      descricao: 'Projetos completos de infraestrutura de rede para empresas de todos os portes.',
      imagem: '/images/rede-bg.jpg',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
          <line x1="6" y1="6" x2="6.01" y2="6"></line>
          <line x1="6" y1="18" x2="6.01" y2="18"></line>
        </svg>
      ),
      destaques: [
        'Cabeamento estruturado certificado',
        'Configuração de servidores e firewalls',
        'Redes Wi-Fi empresariais seguras'
      ]
    },
    {
      id: 'manutencao-celulares-tablets',
      titulo: 'Celulares e Tablets',
      categoria: CATEGORIAS.MANUTENCAO,
      descricao: 'Reparos para smartphones e tablets com diagnóstico especializado e garantia.',
      imagem: '/images/manutencao-celulares-bg.jpg',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
          <line x1="12" y1="18" x2="12.01" y2="18"></line>
        </svg>
      ),
      destaques: [
        'Troca de tela e componentes',
        'Reparo de placas com microsolda',
        'Recuperação por danos de água'
      ]
    },
    {
      id: 'manutencao-notebooks-pcs',
      titulo: 'Notebooks e PCs',
      categoria: CATEGORIAS.MANUTENCAO,
      descricao: 'Soluções para problemas de hardware e software em computadores e laptops.',
      imagem: '/images/manutencao-pcs-bg.jpg',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
      ),
      destaques: [
        'Upgrade de hardware e desempenho',
        'Recuperação de dados críticos',
        'Remoção de malware e vírus'
      ]
    },
    {
      id: 'manutencao-videogames',
      titulo: 'Videogames',
      categoria: CATEGORIAS.MANUTENCAO,
      descricao: 'Reparos especializados para consoles de todas as gerações com garantia de serviço.',
      imagem: '/images/videogames-bg.jpg',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 12h4v4H6zm8-4h4v4h-4z"></path>
          <path d="M15 7v2M15 15v2M9 9H7M17 9h-2M9 15H7"></path>
          <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
        </svg>
      ),
      destaques: [
        'Reparo de unidades ópticas',
        'Manutenção preventiva e limpeza',
        'Substituição de componentes'
      ]
    },
    {
      id: 'manutencao-eletronicos',
      titulo: 'Dispositivos Eletrônicos',
      categoria: CATEGORIAS.MANUTENCAO,
      descricao: 'Serviços de reparo para diversos dispositivos eletrônicos com técnicos especializados.',
      imagem: '/images/manutencao-eletronicos-bg.jpg',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
        </svg>
      ),
      destaques: [
        'Reparos em TVs e equipamentos de áudio',
        'Manutenção de impressoras e periféricos',
        'Conserto de nobreaks e estabilizadores'
      ]
    },
    {
      id: 'automacao-residencial',
      titulo: 'Automação Residencial',
      categoria: CATEGORIAS.INFRAESTRUTURA,
      descricao: 'Transforme sua casa em um ambiente inteligente com sistemas personalizados de controle.',
      imagem: '/images/automacao-bg.jpg',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      ),
      destaques: [
        'Controle de iluminação e climatização',
        'Sistemas de segurança integrados',
        'Assistentes de voz e controle remoto'
      ]
    }
  ];

  // Filtrar serviços pela categoria ativa
  const servicosFiltrados = categoriaAtiva === CATEGORIAS.TODOS 
    ? servicos 
    : servicos.filter(servico => servico.categoria === categoriaAtiva);

  return (
    <div className="min-h-screen">
      {/* Breadcrumbs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link 
                  href="/" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Início
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                  Serviços
                </span>
              </li>
              {categoriaAtiva !== CATEGORIAS.TODOS && (
                <>
                  <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </li>
                  <li>
                    <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                      {NOME_CATEGORIAS[categoriaAtiva]}
                    </span>
                  </li>
                </>
              )}
            </ol>
          </nav>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className={`relative py-8 px-4 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
        <div className="container mx-auto text-center max-w-3xl">
          <h1 className={`text-3xl md:text-4xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Soluções Tecnológicas<br />
            <span className="text-blue-600">Para Pessoas e Empresas</span>
          </h1>
          <p className={`text-base md:text-lg mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Oferecemos serviços especializados de desenvolvimento, manutenção e
            infraestrutura com qualidade e garantia.
          </p>
        </div>
      </section>

      {/* Navegação de categorias - se torna sticky no scroll */}
      <div className={`py-4 border-b ${
        theme === 'dark' 
          ? 'bg-gray-900 border-gray-700' 
          : 'bg-white border-gray-200'
        } ${navSticky ? 'sticky top-0 z-30 shadow-md' : ''}`}
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {Object.entries(NOME_CATEGORIAS).map(([id, nome]) => (
              <motion.button
                key={id}
                whileTap={{ scale: 0.95 }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  categoriaAtiva === id
                    ? theme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                onClick={() => setCategoriaAtiva(id)}
                aria-pressed={categoriaAtiva === id}
                aria-label={`Filtrar por ${nome}`}
              >
                {nome}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Lista de serviços */}
      <section className={`py-12 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {servicosFiltrados.map((servico) => (
              <motion.div 
                key={servico.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                <Link href={`/servicos/${servico.id}`} className="h-full">
                  <div className={`transition-all duration-300 ease-in-out ${
                    theme === 'dark' 
                      ? 'bg-gray-800 text-white border border-gray-700' 
                      : 'bg-white text-gray-900 border border-gray-200'
                  } rounded-lg shadow-lg overflow-hidden hover:shadow-xl h-full flex flex-col ${
                    categoriaAtiva !== CATEGORIAS.TODOS && categoriaAtiva !== servico.categoria ? 'hidden' : ''
                  }`}>
                    <div className="relative w-full h-48 overflow-hidden">
                      <OptimizedImage
                        src={servico.imagem}
                        alt={servico.titulo}
                        fill
                        className="object-cover transform transition-transform duration-500 hover:scale-110"
                        priority={servicosFiltrados.indexOf(servico) < 3}
                        isLCP={servicosFiltrados.indexOf(servico) === 0}
                        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                        style={{ minHeight: "192px" }}
                      />
                      <div className={`absolute inset-0 ${
                        theme === 'dark' 
                          ? 'bg-gradient-to-t from-gray-900 to-transparent opacity-70' 
                          : 'bg-gradient-to-t from-gray-900/50 to-transparent'
                      }`}></div>
                      
                      {/* Badge de categoria */}
                      <div className="absolute top-3 right-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          servico.categoria === CATEGORIAS.DESENVOLVIMENTO 
                            ? 'bg-green-100 text-green-800' 
                            : servico.categoria === CATEGORIAS.MANUTENCAO
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-amber-100 text-amber-800'
                        }`}>
                          {NOME_CATEGORIAS[servico.categoria]}
                        </span>
                      </div>
                    </div>
                    
                    {/* Header com ícone e título */}
                    <div className="p-4 flex-grow">
                      <div className="flex items-center mb-2">
                        <div className={`p-1.5 rounded-full mr-2 ${
                          servico.categoria === CATEGORIAS.DESENVOLVIMENTO
                            ? 'bg-green-100 text-green-700' 
                            : servico.categoria === CATEGORIAS.MANUTENCAO
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-amber-100 text-amber-700'
                        }`}>
                          {servico.icone}
                        </div>
                        <h3 className={`text-lg font-bold ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {servico.titulo}
                        </h3>
                      </div>
                      
                      {/* Descrição */}
                      <p className={`text-sm mb-4 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {servico.descricao}
                      </p>
                      
                      {/* Lista de destaques */}
                      <ul className={`text-xs space-y-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {servico.destaques.map((destaque, index) => (
                          <li key={index} className="flex items-start">
                            <svg className="w-3.5 h-3.5 mr-1.5 mt-0.5 flex-shrink-0 text-blue-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {destaque}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Rodapé com link */}
                    <div className={`px-4 py-3 border-t ${
                      theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
                    }`}>
                      <div className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                      } flex items-center`}>
                        Saiba mais
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-blue-50'}`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Não encontrou o que procura?
          </h2>
          <p className={`text-lg mb-8 max-w-2xl mx-auto ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Entre em contato conosco para discutir suas necessidades específicas. 
            Oferecemos soluções personalizadas para qualquer desafio tecnológico.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a 
              href="/contato" 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-blue-600 text-white font-bold rounded-lg shadow-lg hover:bg-blue-700 transition-all"
            >
              Fale Conosco
            </motion.a>
            <motion.a 
              href="tel:+551932060706" 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-8 py-3 font-bold rounded-lg shadow-lg transition-all flex items-center justify-center ${
                theme === 'dark' 
                  ? 'bg-gray-800 text-white hover:bg-gray-700' 
                  : 'bg-white text-gray-900 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              (19) 3206-0706
            </motion.a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicosPage;